import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface WithdrawalReportAlertContextType {
  showSuccessAlert: boolean;
  showLoadingAlert: boolean;
  downloadUrl: string | null;
  setLoadingAlert: () => void;
  setSuccessAlert: (url: string) => void;
  closeSuccessAlert: () => void;
  closeLoadingAlert: () => void;
}

const WithdrawalReportAlertContext = createContext<
  WithdrawalReportAlertContextType | undefined
>(undefined);

export function WithdrawalReportAlertProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showLoadingAlert, setShowLoadingAlert] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const setLoadingAlert = useCallback(() => {
    setShowLoadingAlert(true);
    setShowSuccessAlert(false);
    setDownloadUrl(null);
  }, []);

  const setSuccessAlert = useCallback((url: string) => {
    setDownloadUrl(url);
    setShowLoadingAlert(false);
    setShowSuccessAlert(true);
  }, []);

  const closeSuccessAlert = useCallback(() => {
    setShowSuccessAlert(false);
    setDownloadUrl(null);
  }, []);

  const closeLoadingAlert = useCallback(() => {
    setShowLoadingAlert(false);
  }, []);

  const value = useMemo(
    () => ({
      showSuccessAlert,
      showLoadingAlert,
      downloadUrl,
      setLoadingAlert,
      setSuccessAlert,
      closeSuccessAlert,
      closeLoadingAlert,
    }),
    [
      showSuccessAlert,
      showLoadingAlert,
      downloadUrl,
      setLoadingAlert,
      setSuccessAlert,
      closeSuccessAlert,
      closeLoadingAlert,
    ],
  );

  return (
    <WithdrawalReportAlertContext.Provider value={value}>
      {children}
    </WithdrawalReportAlertContext.Provider>
  );
}

export function useWithdrawalReportAlert(): WithdrawalReportAlertContextType {
  const context = useContext(WithdrawalReportAlertContext);
  if (!context) {
    throw new Error(
      'useWithdrawalReportAlert must be used within WithdrawalReportAlertProvider',
    );
  }
  return context;
}
